import { CreditCard, ExternalLink } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  useAssignSponsorship,
  useBillingPortal,
  useBuyMembership,
  useCancelSubscription,
  useUpdateDonationAmount,
  useMyBilling,
  useMySponsorships,
} from "@/features/payments/hooks/usePayments";
import { dollars } from "@/features/payments/payments.types";
import type {
  MyBilling,
  SponsoredMembership,
  Subscription,
} from "@/features/payments/payments.types";
import { Link } from "react-router-dom";
import { useApplyCta } from "@/features/members/hooks/useApplyCta";
import { DonationFields, SponsorButton } from "@/components/billing/Giving";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";

const sectionTitleClass = "text-base";

/**
 * Membership & billing: the one place money happens, for every tier.
 *
 * Three independent items, because they are independent decisions. **Your own
 * membership** is gated on an approved application, since a membership is a
 * directory listing and listings are reviewed. **Sponsorships** and
 * **donations** are gated on nothing — paying for somebody else needs no
 * permission, so a free-tier member can do both without buying a membership
 * they have not been approved for.
 *
 * This replaces the emailed single-use `/pay/<token>` page for anyone with an
 * account: approval now points here.
 */
export default function BillingPage() {
  const { data: billing, isPending } = useMyBilling();

  return (
    <div className="w-full max-w-2xl space-y-4">
      {/* Its own heading, like every other page: the layout no longer renders
          one, because on the admin pages that produced two. */}
      <h1 className="text-2xl">Membership &amp; billing</h1>
      {isPending || !billing ? (
        <>
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-28 w-full" />
        </>
      ) : (
        <>
          <MembershipCard billing={billing} />
          <AddSubscriptions billing={billing} />
        </>
      )}
    </div>
  );
}

/**
 * What you are paying for, one row each.
 *
 * Read from Stripe rather than from our own columns, because a member can hold
 * several at once — their own membership, a sponsorship-only subscription
 * bought without one, a standing donation — and `Member` only ever remembered
 * the last id it saw. Each row says what it is, what state it is in, when it
 * next charges, and how to stop it.
 *
 * The seats a sponsor has paid for sit in the same list, named from here:
 * "who is this for" is a question about the thing you are looking at, not a
 * separate card further down the page.
 */
/**
 * What to say when Stripe has nothing to list.
 *
 * Every imported member sits in exactly this position until their subscription
 * moves across: paid in our own records, carrying the renewal date the
 * invitation promised them, and with nothing in Stripe behind it. There are
 * 254 of them. The old sentence was written for the free tier and told all of
 * them they "aren't paying for anything yet", directly beneath "You have the
 * annual membership".
 *
 * The date was already on the payload as `renewsAt` and simply had nowhere to
 * render, because a renewal is drawn on a subscription row and there is no
 * row. This clears itself at the cutover: once the subscription exists Stripe
 * has something to list, `nothingToShow` is false, and this text is gone.
 */
function nothingToShowNote(billing: MyBilling) {
  // Before the tier: the importer left lapsed members labelled `paid`, and the
  // paid line below told them the old site was still billing them.
  if (billing.status === "lapsed") return "Nothing is being charged: your membership has lapsed.";
  if (billing.tier === "gift") return "Nothing to pay: this membership is a gift.";
  if (billing.tier === "paid") {
    const billed =
      "It is still billed through the old site, so there is nothing to manage here yet.";
    return billing.renewsAt
      ? `Your membership runs to ${new Date(billing.renewsAt).toLocaleDateString()}. ${billed}`
      : `Your membership carries over from the old site. ${billed}`;
  }
  return "You aren't paying for anything yet.";
}

function MembershipCard({ billing }: { billing: MyBilling }) {
  const portal = useBillingPortal();
  const { data: sponsorships } = useMySponsorships();

  const description =
    billing.status === "lapsed"
      ? "Your membership has lapsed. Renew it to bring your directory listing back."
      : billing.tier === "paid"
        ? "You have the annual membership."
        : billing.tier === "gift"
          ? billing.giftSource === "sponsorship"
            ? "Your membership is being paid for by a sponsor. It has the same access as a paid membership, and lasts as long as their payment does."
            : "Your membership was granted at no cost. It has the same access as a paid membership, and does not expire."
          : billing.approved
            ? "Your application is approved. Subscribe to be listed in the directory with full access."
            : "You're on the free preview: a sample of the directory, the listserv and its archive.";

  const seats = sponsorships?.sponsorships ?? [];
  const seatRowIndex = billing.subscriptions.findIndex(
    (s) => s.kind === "sponsorship" || (s.kind === "membership" && s.quantity > 1)
  );
  const nothingToShow =
    billing.subscriptions.length === 0 && seats.length === 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className={sectionTitleClass}>
          <CreditCard className="mr-1.5 inline size-4" />
          Your membership
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {!billing.approved && (
          <p className="text-muted-foreground text-sm">
            {billing.applicationStatus === "pending"
              ? "Your application is with an administrator. We'll email you when it's been reviewed."
              : "A membership is a directory listing, so it starts with an application."}
          </p>
        )}

        {nothingToShow ? (
          <p className="text-muted-foreground text-sm">{nothingToShowNote(billing)}</p>
        ) : (
          <ul className="space-y-2">
            {billing.subscriptions.map((subscription, i) => (
              <SubscriptionRow
                key={subscription.id}
                subscription={subscription}
                // The seats sit inside the subscription that pays for them, on
                // the first row that could be paying for any: a
                // sponsorship-only subscription, or a membership bought with
                // extra quantity. Listing them alongside read as several
                // subscriptions when it is one.
                seats={i === seatRowIndex ? seats : []}
              />
            ))}
            {seatRowIndex === -1 &&
              seats.map((seat) => (
                <li
                  key={seat.id}
                  className="rounded-md border px-3 py-2.5 text-sm"
                >
                  <SponsoredSeat seat={seat} />
                </li>
              ))}
          </ul>
        )}

        {/* Anything the rows cannot do — cards, invoices, receipts — lives in
            Stripe's own portal rather than being rebuilt here. */}
        {billing.hasStripeCustomer && (
          <Button
            variant="ghost"
            size="sm"
            disabled={portal.isPending}
            onClick={() =>
              portal.mutate(undefined, {
                onError: (e) => toast.error(e.message),
              })
            }
          >
            {portal.isPending && <Spinner data-slot="icon" />}
            Manage cards and invoices on Stripe
          </Button>
        )}

        {billing.payments.length > 0 && (
          <details className="text-muted-foreground text-sm">
            <summary className="cursor-pointer">Payment history</summary>
            <div className="mt-2 space-y-1">
              {billing.payments.map((p) => (
                <p key={p.id} className="flex flex-wrap items-center gap-2">
                  <span>
                    {new Date(p.created_at).toLocaleDateString()}{" · "}
                    {dollars(p.amount_cents)} {p.kind}
                    {p.simulated ? " (simulated)" : ""}
                  </span>
                  {/* Only Stripe-billed money has an invoice. The imported
                      history was charged by Squarespace as bare charges, so
                      those rows have nothing to offer and say nothing. */}
                  {p.invoiceUrl && (
                    <a
                      href={p.invoiceUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 underline underline-offset-4"
                    >
                      Invoice <ExternalLink className="size-3" />
                    </a>
                  )}
                </p>
              ))}
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

const STATUS_LABEL: Record<string, string> = {
  active: "Active",
  trialing: "Trial",
  past_due: "Past due",
  unpaid: "Unpaid",
  canceled: "Cancelled",
  incomplete: "Incomplete",
};

/** Green for fine, outline for over — a badge nobody has to decode. */
function StatusBadge({ status }: { status: string }) {
  const label = STATUS_LABEL[status] ?? status;
  const bad = ["past_due", "unpaid", "incomplete", "canceled"].includes(status);
  return (
    <Badge variant={bad ? "destructive" : "secondary"} className="shrink-0">
      {label}
    </Badge>
  );
}

function SubscriptionRow({
  subscription,
  seats,
}: {
  subscription: Subscription;
  seats: SponsoredMembership[];
}) {
  const cancel = useCancelSubscription();
  const ends = subscription.currentPeriodEnd
    ? new Date(subscription.currentPeriodEnd).toLocaleDateString()
    : null;
  const sponsored = subscription.kind === "sponsorship";

  return (
    <li className="rounded-md border px-3 py-2.5 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium">
            {subscription.kind === "donation" ? "Donation" : "Membership"}
          </span>
          {/* A sponsorship *is* a membership — for somebody else. Same word,
              with the badge saying whose. */}
          {sponsored && <Badge variant="outline">Sponsored</Badge>}
          <StatusBadge status={subscription.status} />
        </span>
        <span className="text-muted-foreground">
          {dollars(subscription.amountCents)}
          {subscription.interval ? `/${subscription.interval}` : ""}
        </span>
      </div>

      <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
        <span className="text-muted-foreground text-xs">
          {ends
            ? subscription.cancelAtPeriodEnd
              ? `Ends ${ends}, and it will not renew`
              : `Renews ${ends}`
            : "No renewal date"}
        </span>
        {["active", "trialing", "past_due"].includes(subscription.status) && (
          <Button
            variant="ghost"
            size="sm"
            disabled={cancel.isPending}
            onClick={() =>
              cancel.mutate(
                { id: subscription.id, resume: subscription.cancelAtPeriodEnd },
                {
                  onSuccess: () =>
                    toast.success(
                      subscription.cancelAtPeriodEnd
                        ? "It will renew again"
                        : "It will not renew"
                    ),
                  onError: (e) => toast.error(e.message),
                }
              )
            }
          >
            {cancel.isPending && <Spinner data-slot="icon" />}
            {subscription.cancelAtPeriodEnd ? "Resume" : "Cancel"}
          </Button>
        )}
      </div>

      {subscription.kind === "donation" && (
        <DonationAmount subscription={subscription} />
      )}

      {seats.length > 0 && (
        <ul className="mt-2 space-y-2 border-t pt-2">
          {seats.map((seat) => (
            <li key={seat.id}>
              <SponsoredSeat seat={seat} />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/** Change what a donation gives, without cancelling and starting again. */
function DonationAmount({ subscription }: { subscription: Subscription }) {
  const update = useUpdateDonationAmount();
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(
    String(subscription.amountCents / 100)
  );

  const value = Number(amount);
  const valid = Number.isFinite(value) && value >= 5 && value <= 10000;

  if (!editing) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="mt-1 px-0"
        onClick={() => setEditing(true)}
      >
        Change amount
      </Button>
    );
  }

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 border-t pt-2">
      <span className="text-muted-foreground text-sm">$</span>
      <Input
        aria-label="Donation amount in dollars"
        inputMode="decimal"
        className="h-9 w-24"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
      />
      <span className="text-muted-foreground text-sm">
        {subscription.interval === "month" ? "a month" : "a year"}
      </span>
      <Button
        size="sm"
        disabled={!valid || update.isPending}
        onClick={() =>
          update.mutate(
            { id: subscription.id, amountCents: Math.round(value * 100) },
            {
              onSuccess: () => {
                setEditing(false);
                toast.success("Your next donation will be the new amount");
              },
              onError: (e) => toast.error(e.message),
            }
          )
        }
      >
        {update.isPending && <Spinner data-slot="icon" />}
        Save
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
        Cancel
      </Button>
    </div>
  );
}

/** One seat a sponsor has paid for, named from inside the subscription. */
function SponsoredSeat({ seat }: { seat: SponsoredMembership }) {
  const assign = useAssignSponsorship();
  const [email, setEmail] = useState("");

  const submit = () =>
    assign.mutate(
      { id: seat.id, email: email.trim() },
      {
        onSuccess: () => {
          setEmail("");
          toast.success("They have been emailed");
        },
        onError: (error) => toast.error(error.message),
      }
    );

  if (seat.status === "claimed") {
    return (
      <p className="text-muted-foreground text-xs">
        <span className="text-foreground font-medium">
          {seat.beneficiary_name || seat.beneficiary_email}
        </span>{" "}
        has this membership.
      </p>
    );
  }

  return (
    <div className="space-y-1">
      <p className="text-muted-foreground text-xs">
        {seat.status === "assigned" ? (
          <>
            Waiting for{" "}
            <span className="text-foreground">{seat.beneficiary_email}</span> to
            create an account. You can change who it&apos;s for until they do.
          </>
        ) : (
          "Not assigned yet. Name someone whenever you like."
        )}
      </p>
      <div className="flex flex-wrap gap-2">
        <Input
          type="email"
          aria-label="Beneficiary email address"
          placeholder={seat.beneficiary_email || "their@email.address"}
          className="h-9 min-w-56 flex-1"
          value={email}
          disabled={assign.isPending}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
        />
        <Button
          variant="outline"
          disabled={assign.isPending || !email.trim()}
          onClick={submit}
        >
          {assign.isPending && <Spinner data-slot="icon" />}
          {seat.status === "assigned" ? "Change" : "Name someone"}
        </Button>
      </div>
    </div>
  );
}

/**
 * The ways to start paying for something, under the list of what you already
 * pay for.
 *
 * Three independent decisions, so three buttons rather than one flow.
 * **Subscribing** is gated on an approved application, because a membership is
 * a directory listing and listings are reviewed; before approval the same
 * button is the application itself. **Sponsoring** and **donating** are gated
 * on nothing — paying for somebody else needs no permission.
 */
function AddSubscriptions({ billing }: { billing: MyBilling }) {
  const buy = useBuyMembership();
  const applyLabel = useApplyCta();

  return (
    <Card>
      <CardHeader>
        <CardTitle className={sectionTitleClass}>Add a subscription</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {/* Before approval this is the application; after it, the same place
              in the page becomes the thing the application was for. It was
              also `disabled` while an application waited, which left the one
              blue button on the page inert and unexplained: the way to change
              a waiting application is to open it. */}
          {!billing.approved ? (
            <Button asChild variant="brand">
              <Link to="/apply">{applyLabel}</Link>
            </Button>
          ) : billing.canBuyMembership ? (
            <Button
              disabled={buy.isPending}
              onClick={() =>
                buy.mutate({}, { onError: (e) => toast.error(e.message) })
              }
            >
              {buy.isPending && <Spinner data-slot="icon" />}
              {billing.status === "lapsed"
                ? "Renew membership"
                : billing.tier === "gift"
                  ? "Subscribe anyway"
                  : "Subscribe"}
            </Button>
          ) : null}
          {billing.canBuyMembership && (
            <span className="text-muted-foreground text-xs">
              {dollars(billing.pricing.membershipCents)}/year plus a one-off{" "}
              {dollars(billing.pricing.serviceFeeCents)} service fee.
            </span>
          )}
        </div>

        <SponsorButton />

        <DonationFields />
      </CardContent>
    </Card>
  );
}

